import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { GenieCard } from "@/components/GenieLauncher";
import { SocialGate } from "@/components/SocialCTA";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, TrendingUp, Users, Target, Sparkles, Handshake, BarChart3, Camera, MapPin, Calendar, Building2, Loader2, ImagePlus } from "lucide-react";

const solutions = [
  { icon: Zap, t: "Experiential Marketing", d: "IRL touchpoints people photograph and share.", cls: "card-pink" },
  { icon: Handshake, t: "Sponsorships", d: "Partner with events aligned to your audience.", cls: "card-lime" },
  { icon: Users, t: "Campus Activations", d: "Own the college calendar across India.", cls: "card-orange" },
  { icon: Target, t: "Product Sampling", d: "Products in the hands that matter.", cls: "card-purple" },
  { icon: Sparkles, t: "Influencer Campaigns", d: "Micro-creators who convert.", cls: "card-purple" },
  { icon: Camera, t: "AI Photo Campaigns", d: "Every photo becomes a branded moment.", cls: "card-lime" },
  { icon: TrendingUp, t: "Lead Generation", d: "Real, verified leads from events.", cls: "card-pink" },
  { icon: BarChart3, t: "Audience Engagement", d: "Interactive campaigns with outcomes.", cls: "card-orange" },
];

const isPast = (e) => { if (!e?.date) return false; const d = new Date(e.date); d.setHours(23, 59, 59, 999); return d < new Date(); };

function BrandWall() {
  const [brands, setBrands] = useState([]);
  useEffect(() => { api.get("/brands/partners").then(({ data }) => setBrands(data || [])).catch(() => {}); }, []);
  if (brands.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="brand-wall">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-lime mb-2">Trusted by</div>
      <h2 className="font-display text-4xl md:text-5xl ribbon-line">Brands working with Eventom</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-12">
        {brands.map((b) => (
          <div key={b.id} className="glass p-4 card-lift flex flex-col items-center text-center gap-2" data-testid={`brand-wall-${b.id}`}>
            <div className="w-full h-16 flex items-center justify-center">
              {b.logo_url ? (
                <img src={b.logo_url} alt={b.brand_name} loading="lazy" className="max-h-16 max-w-full object-contain" data-testid={`brand-logo-${b.id}`} />
              ) : (
                <span className="font-display text-2xl">{b.brand_name}</span>
              )}
            </div>
            <div className="font-heading font-bold text-xs">{b.brand_name}</div>
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted">{b.industry}{b.target_cities ? ` · ${b.target_cities}` : ""}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ForBrands() {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ brand_name: "", industry: "", campaign_budget: "", target_events: "", target_cities: "", contact_email: "", contact_mobile: "", logo_url: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [gate, setGate] = useState(false);

  useEffect(() => { api.get("/events?limit=100").then(({ data }) => setEvents(data)).catch(() => {}); }, []);
  const upcoming = events.filter((e) => !isPast(e)).slice(0, 6);
  const past = events.filter(isPast).slice(0, 8);

  const pickLogo = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 400 * 1024) return toast.error("Logo must be under 400KB");
    const r = new FileReader();
    r.onload = () => setForm((s) => ({ ...s, logo_url: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/brands/leads", form); setDone(true); setGate(true); toast.success("Received — we'll be in touch"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Layout>
      {/* THEMED PURPLE STRIP — brand magazine thread */}
      <section className="themed-purple sticker-wall py-8 overflow-hidden" data-testid="brands-themed-strip">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/85">● For Brands & Sponsors</div>
            <h1 className="font-fancy uppercase text-white leading-[0.9] mt-1" style={{ fontSize: "clamp(32px, 5vw, 56px)", textShadow: "3px 3px 0 rgba(0,0,0,0.3)" }}>
              Sponsor the <span className="text-striped">Moment.</span>
            </h1>
          </div>
          <a href="#opps" className="btn-pill-white !text-[11px]">See opportunities →</a>
        </div>
      </section>
      <SocialGate open={gate} onClose={() => setGate(false)} title="Brief received. Stay close."
        sub="Sponsorship slots, fest calendars and activation openings go out in our communities first." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6"><GenieCard context="brands" sub="Your AI activation scout — fests, audiences, ROI" testId="genie-card-brands" /></div>

      <section className="relative overflow-hidden py-16">
        <div className="blob blob-lg blob-purple blob-float" style={{ top: "-240px", left: "-140px" }} />
        <div className="blob blob-md blob-lime blob-float delay-2" style={{ bottom: "-160px", right: "-60px" }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <span className="tile w-14 h-14 mb-4"><Building2 className="w-6 h-6 relative z-10" /></span>
            <div className="chip !text-skyx !border-skyx/40 mb-4">For Brands & Sponsors</div>
            <h1 className="font-fancy text-5xl md:text-7xl leading-[0.92]">
              Real crowds.<br /><span className="marker-blue">Real reach.</span><br /><span className="marker-pink">Real ROI.</span>
            </h1>
            <p className="mt-5 text-text/85 max-w-xl">Sponsor live experiences, run city activations and own the college circuit — measured with Eventom AI.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#opps" data-testid="brands-cta" className="btn-lime"><Target className="w-4 h-4" /> See opportunities</a>
              <a href="#list" data-testid="brands-list-cta" className="btn-ghost"><ImagePlus className="w-4 h-4" /> Get listed with your logo</a>
            </div>
            <div className="mt-4 chip !text-lime !border-lime/40" data-testid="brand-coming-soon">Live now: brand dashboard, opportunities & AI activation matching</div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[["3X", "Avg impact vs digital-only", "card-purple"], ["1.2M+", "Attendee reach", "card-lime"], ["85%", "Photo pickup", "card-pink"], ["40+", "Cities on the circuit", "card-orange"]].map(([k, v, cls]) => (
              <div key={k} className={`${cls} p-5`}>
                <div className="font-display text-4xl md:text-5xl leading-none">{k}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-white/75">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandWall />

      <section id="opps" className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-lime mb-2">Upcoming opportunities</div>
        <h2 className="font-display text-4xl md:text-5xl ribbon-line">Sponsor the moments India is talking about.</h2>
        {upcoming.length === 0 ? (
          <div className="glass p-6 font-mono text-sm text-muted mt-12">No upcoming opportunities right now — check back soon.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {upcoming.map((e, i) => (
              <div key={e.id} className="glass overflow-hidden card-lift" data-testid={`brand-opp-${e.id}`}>
                <div className="aspect-[16/9] overflow-hidden bg-black/50">{e.banner && <img src={e.banner} alt={e.title} loading="lazy" className="w-full h-full object-cover" />}</div>
                <div className="p-5">
                  <div className="font-display text-2xl leading-tight break-words">{e.title}</div>
                  <div className="mt-2 flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-widest text-muted">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-lime" />{new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-pink" />{e.city}</span>
                    <span className="chip !text-[9px] !text-skyx !border-skyx/40">{e.category}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="glass !rounded-xl p-2"><div className="text-muted text-[9px] font-mono uppercase">Est. footfall</div><div className="font-display text-xl text-lime">{(2000 + i * 500).toLocaleString()}</div></div>
                    <div className="glass !rounded-xl p-2"><div className="text-muted text-[9px] font-mono uppercase">Sponsor slots</div><div className="font-display text-xl text-pink">{4 + (i % 3)}</div></div>
                  </div>
                  <a href={`https://wa.me/917250120666?text=${encodeURIComponent(`Hi Eventom! Interested in sponsoring ${e.title}.`)}`} target="_blank" rel="noreferrer" data-testid={`brand-opp-cta-${e.id}`} className="mt-4 w-full btn-primary justify-center !py-2.5 !text-xs">Contact sales</a>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-pink mb-2">Brand solutions</div>
        <h2 className="font-display text-4xl md:text-5xl ribbon-line">How we make brands unmissable.</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {solutions.map((s) => (
            <div key={s.t} className={`${s.cls} p-5 min-h-[190px] flex flex-col gap-2 card-lift`} data-testid={`brand-sol-${s.t.replace(/\s+/g, '-').toLowerCase()}`}>
              <s.icon className="w-6 h-6 text-lime" />
              <div className="font-display text-2xl leading-tight break-words">{s.t}</div>
              <div className="text-sm text-white/80 break-words">{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      {past.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-orangex mb-2">Past activations</div>
          <h2 className="font-display text-3xl md:text-4xl mb-8">Where we've been.</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {past.map((e) => (
              <div key={e.id} className="glass overflow-hidden" data-testid={`past-brand-${e.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-black/50">{e.banner && <img src={e.banner} alt={e.title} loading="lazy" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />}</div>
                <div className="p-3">
                  <div className="font-heading font-bold text-sm leading-tight line-clamp-2 min-h-[34px]">{e.title}</div>
                  <div className="mt-1 text-[10px] font-mono uppercase text-muted">{e.city} · {new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="list" className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        {done ? (
          <div className="glass p-8 text-center" data-testid="brand-lead-success">
            <span className="tile tile-lime w-14 h-14 mx-auto mb-4"><Sparkles className="w-6 h-6 relative z-10" /></span>
            <div className="font-display text-3xl">We got you.</div>
            <div className="text-muted mt-2">Our brand team reaches out within 48 hours. Once approved, your logo joins the brand wall above.</div>
          </div>
        ) : (
          <form onSubmit={submit} className="glass p-6 space-y-3" data-testid="brand-lead-form">
            <div className="font-display text-3xl">Let's build something.</div>
            <div className="text-xs text-muted mb-1">Submit once — our team replies and your logo goes on the wall after approval.</div>
            <input required data-testid="brand-name" placeholder="Brand name" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="input-dark" />
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="brand-industry" placeholder="Industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="input-dark" />
              <input required data-testid="brand-budget" placeholder="Budget (e.g. 5-10L)" value={form.campaign_budget} onChange={(e) => setForm({ ...form, campaign_budget: e.target.value })} className="input-dark" />
            </div>
            <input data-testid="brand-target-events" placeholder="Target events (e.g. college fests, concerts)" value={form.target_events} onChange={(e) => setForm({ ...form, target_events: e.target.value })} className="input-dark" />
            <input data-testid="brand-target-cities" placeholder="Target cities" value={form.target_cities} onChange={(e) => setForm({ ...form, target_cities: e.target.value })} className="input-dark" />
            <div className="glass !rounded-2xl p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden shrink-0">
                {form.logo_url ? <img src={form.logo_url} alt="" className="max-h-full max-w-full object-contain" data-testid="brand-logo-preview" /> : <ImagePlus className="w-5 h-5 text-muted" />}
              </div>
              <div className="min-w-0">
                <label className="btn-ghost !py-2 !px-3 !text-[11px] cursor-pointer" data-testid="brand-logo-upload">
                  Upload your logo
                  <input type="file" accept="image/*" className="hidden" onChange={pickLogo} data-testid="brand-logo-input" />
                </label>
                <div className="text-[10px] text-muted mt-1">PNG or SVG, under 400KB — shown on the brand wall.</div>
              </div>
            </div>
            <input data-testid="brand-website" placeholder="Website (optional)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input-dark" />
            <div className="grid grid-cols-2 gap-3">
              <input required type="email" data-testid="brand-email" placeholder="Contact email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="input-dark" />
              <input required data-testid="brand-mobile" placeholder="Contact mobile" value={form.contact_mobile} onChange={(e) => setForm({ ...form, contact_mobile: e.target.value })} className="input-dark" />
            </div>
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="brand-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />} Submit brand interest
            </button>
          </form>
        )}
      </section>
    </Layout>
  );
}
